import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, Button } from '@material-ui/core';
import ICommonFormProps from '../../../../yoello-lib/modules/dynamicForms/interfaces/ICommonFormProps';
import TitledContainer from '../../../ui/TitledContainer';
import AddNewUsersContext from '../Dialogues/AddNewUsers/AddNewUsers.context';
import usePermissionChecklist, { Permission } from './usePermissionChecklist';
import FormCheckboxInput from '../../../ui/FormCheckboxInput';
import { PERMISSIONS, PERMISSIONS_LIST } from '../../../../const/permissions.const';
import { FaqLinks, getUrlBasedOnCurrentLanguage } from "../../../../const/faqs-links";
import { useSelector } from 'react-redux';
import { IReduxAppState } from '../../../../reducers';
import { hasSelectedPermission } from '../Dialogues/AddNewUsers/AddNewUsers.actions';



const useStyles = makeStyles({
  button: {
    marginLeft: "1rem",
    '& > span': {
      fontWeight: 400,
    }
  },
  permissionsContainer: {
    margin: '1rem',
    display: 'flex',
    flexDirection: 'column',
    minWidth: "40rem"
  },
  link: {
    marginLeft: "0.5rem",
    color: "#FFFFFF"
  }
});

const PermissionList = (props: ICommonFormProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { dispatch } = useContext(AddNewUsersContext);
  
  const merchantPermissions = useSelector((state: IReduxAppState) => state.permissions);

  const { 
    watch, 
    setValue, 
  } = props;

  const { 
    permissions,
    isSelectAllDisabled,
    isSelectNoneDisabled,
    onSelectAll,
    onSelectNone,
    onPermissionChange, 
    isSelf,
    isSuperuser,
  } = usePermissionChecklist(watch, setValue);

  const renderPermissions = React.useCallback(() => {
    if(!merchantPermissions) return null;
    return permissions.map(({ permission_set, value }: Permission, idx: number) => {
      const keyName = `permissions.[${idx}].value`;
      const isReadOnly = isSelf || //Is user same as logged in merchant?
        (isSuperuser && PERMISSIONS_LIST[permission_set as PERMISSIONS].requiredForSuperuser) || //Is permission in required for superuser list?
        (permission_set === PERMISSIONS.ADMIN && !merchantPermissions[PERMISSIONS.ADMIN]) || !merchantPermissions[permission_set]
      

      const onChange = (e: Event, checked: boolean) => {
        if(isReadOnly) return;
        onPermissionChange(e, permission_set);
      }

      return (
        //readOnly doesn't seem to work for MUI Checkboxes, so isSelf checks are there to emulate its effects
        <>

        <FormCheckboxInput 
          {...props}
          value={value}
          label={t(`permissions.${permission_set}`)}
          key={permission_set}
          keyName={keyName}
          readOnly={isSelf || isReadOnly}
          onChange={onChange}
          disabled={props.disabled || isReadOnly}
          tooltipContent={
            <>
              <span>{t(`permissions.tooltip.${permission_set}`)}</span>
              <span><a className={classes.link} href={getUrlBasedOnCurrentLanguage(FaqLinks.USER_MANAGEMENT.key)}>{t("permissions.findOutMore")}</a></span>
            </>
            
          }
        />
        </>
      );
    })
    //eslint-disable-next-line
  }, [permissions, onPermissionChange, isSelf, t, props])


  useEffect(()=>{
    if(dispatch){
      dispatch(hasSelectedPermission(!isSelectNoneDisabled));
    }
  },[isSelectNoneDisabled, dispatch])

  return permissions ? (
    <TitledContainer
      title={t("userManagement.form.selectPermissions")}
      titleButtons={
        isSelf ? 
          null :
          <>
            <Button
              variant="contained"
              size="small"
              className={classes.button}
              onClick={onSelectAll}
              disabled={isSelectAllDisabled || props.disabled}
            >
              {t('misc.selectAll')}
            </Button>
            <Button
              variant="contained"
              size="small"
              className={classes.button}
              onClick={onSelectNone}
              disabled={isSelectNoneDisabled  || props.disabled}
            >
              {t('misc.selectNone')}
            </Button>
          </>
      }
    >
      <div className={classes.permissionsContainer}>
        {renderPermissions()}
      </div>
    </TitledContainer>
  ) : null;
};

export default PermissionList;
